// EditProfileDialog.js
import React, { useState } from 'react'; // Import useEffect
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Avatar,
  Box,
  IconButton,
  Typography,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import {
  CameraAlt as CameraAltIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { showGlobalNotification } from '../../context/NotificationContext.js';
import { useUser } from '../../context/UserContext';
import { getDocumentUrl } from '../../utils/authUtils';
import NotificationSettingsPanel from '../common/NotificationSettingsPanel';
import { useTour } from '../../tours/TourProvider';

// Changed prop name from profileData to currentUser
function EditProfileDialog({ open, onClose, currentUser, /* setProfileData, authHeader */ }) {
  // Refresh the shared user context after saving so reopening the dialog
  // shows the persisted values instead of the stale pre-edit user object
  const { refreshUser } = useUser();
  // "Reset all tours": every page shows its first-run tour again
  const tour = useTour();

  // State for Profile Picture file (used in the dialog)
  const [profilePicture, setProfilePicture] = useState(null);

  // Local state for form fields (to avoid changing parent state before save)
  // Initialize with currentUser or an empty object if currentUser is null/undefined
  const [localProfileData, setLocalProfileData] = useState(currentUser || {});
  
  
  // State for password fields
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
    showNewPassword: false,
    showConfirmPassword: false,
  });
  
  // State for password validation
  const [passwordError, setPasswordError] = useState('');
  
  // State for notification preferences - will be managed by NotificationSettings component
  const [notificationPrefs, setNotificationPrefs] = useState(null);

  
  // Update localProfileData when currentUser prop changes
  React.useEffect(() => {
    // Only update if currentUser is provided
    if (currentUser) {
      setLocalProfileData(currentUser);
      // Reset notification preferences to let NotificationSettings component handle initialization
      setNotificationPrefs(null);
    } else {
      // If currentUser becomes null (e.g., logout), reset local state
      setLocalProfileData({});
      setNotificationPrefs(null);
    }
  }, [currentUser]); // Update when currentUser changes

  // Handler for Profile Picture Change
  const handleProfilePictureChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setProfilePicture(event.target.files[0]);
    }
  };
  
  // Handler for password visibility toggle
  const handlePasswordVisibility = (field) => {
    if (field === 'new') {
      setPasswordData({
        ...passwordData,
        showNewPassword: !passwordData.showNewPassword,
      });
    } else if (field === 'confirm') {
      setPasswordData({
        ...passwordData,
        showConfirmPassword: !passwordData.showConfirmPassword,
      });
    }
  };
  
  // Handler for password change
  const handlePasswordChange = (e, field) => {
    const value = e.target.value;
    setPasswordData({
      ...passwordData,
      [field]: value,
    });
    
    // Clear error when typing
    if (passwordError) {
      setPasswordError('');
    }
  };
  
  // Handler for notification preference changes from NotificationSettings component
  const handleNotificationPreferencesChange = (newPreferences) => {
    setNotificationPrefs(newPreferences);
  };

  // Function to validate passwords
  const validatePasswords = () => {
    if (passwordData.newPassword && passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    
    if (passwordData.newPassword && passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    
    return true;
  };

  // Function to handle saving profile changes
  const handleSaveProfile = () => {
    // Validate passwords if a new password is being set
    if (passwordData.newPassword && !validatePasswords()) {
      return;
    }
    
    const formData = new FormData();
    
    // Always include these fields
    formData.append('firstName', localProfileData.firstName);
    formData.append('lastName', localProfileData.lastName);
    formData.append('username', localProfileData.username);
    formData.append('phone', localProfileData.phone || localProfileData.phoneNumber || '');
    
    
    // Include password only if it's being changed
    if (passwordData.newPassword) {
      formData.append('password', passwordData.newPassword);
    }
    
    // Include notification preferences if they exist
    if (notificationPrefs) {
      formData.append('notificationPreferences', JSON.stringify(notificationPrefs));
    }
    
    // Include SP-specific fields only if user is an SP
    if (localProfileData.primaryRole === 'SP') {
      formData.append('companyName', localProfileData.companyName || '');
      formData.append('position', localProfileData.position || '');
      formData.append('companyAddress', localProfileData.companyAddress || '');
      formData.append('companyAddress2', localProfileData.companyAddress2 || '');
      formData.append('city', localProfileData.city || '');
      formData.append('state', localProfileData.state || '');
      formData.append('zip', localProfileData.zip || '');
      formData.append('mainLinePhoneNumber', localProfileData.mainLinePhoneNumber || '');
      formData.append('dispatchPhoneNumber', localProfileData.dispatchPhoneNumber || '');
      formData.append('answeringServicePhoneNumber', localProfileData.answeringServicePhoneNumber || '');
      formData.append('towingLicenseNumber', localProfileData.towingLicenseNumber || '');
      formData.append('dotNumber', localProfileData.dotNumber || '');
    }

    // Include profile picture if changed
    if (profilePicture) {
      formData.append('profilePicture', profilePicture);
    }

    // Log the form data for debugging
    console.log('Form data being sent:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }
    
    // DEBUG: Log notification preferences specifically
    console.log('DEBUG: EditProfileDialog - Notification preferences in payload:', {
      notificationPrefs: notificationPrefs,
      notificationPrefsString: notificationPrefs ? JSON.stringify(notificationPrefs) : 'null'
    });

    // Send the updated profile data to the backend
    fetch(`${process.env.REACT_APP_API_URL}/api/users/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        // Do not set Content-Type when sending FormData
        // The browser will automatically set it to 'multipart/form-data' with the appropriate boundary
      },
      body: formData,
    })
      .then(async (res) => {
        if (!res.ok) {
          // Try to get error details from response
          let errorDetails = '';
          try {
            const errorData = await res.json();
            errorDetails = JSON.stringify(errorData);
          } catch (e) {
            errorDetails = await res.text();
          }
          throw new Error(`Network response was not ok (${res.status}): ${errorDetails}`);
        }
        return res.json();
      })
      .then((data) => {
        // Pull the freshly saved user into the shared context
        if (refreshUser) refreshUser();
        onClose();
        setProfilePicture(null); // Reset profile picture file
        // Reset password fields
        setPasswordData({
          newPassword: '',
          confirmPassword: '',
          showNewPassword: false,
          showConfirmPassword: false,
        });
      })
      .catch((error) => {
        console.error('Error updating profile:', error);
        showGlobalNotification(`Failed to update profile: ${error.message}`, 'error');
        // Handle error as needed
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Profile</DialogTitle>
      <DialogContent>
        {/* Profile Picture with Upload Option */}
        <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
          <Box position="relative">
            <Avatar
              alt="Profile Picture"
              src={
                profilePicture
                  ? URL.createObjectURL(profilePicture)
                  : localProfileData.profilePicture
                    ? getDocumentUrl(localProfileData.profilePicture) // Use getDocumentUrl here
                    : '/images/default-profile.png'
              }
              sx={{ width: 100, height: 100 }}
            />
            <IconButton
              color="primary"
              aria-label="upload picture"
              component="label"
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: 'white',
                },
              }}
            >
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={handleProfilePictureChange}
              />
              <CameraAltIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Basic User Information */}
        <Typography variant="h6" gutterBottom>
          User Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              label="First Name"
              fullWidth
              value={localProfileData.firstName || ''}
              onChange={(e) =>
                setLocalProfileData({ ...localProfileData, firstName: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              label="Last Name"
              fullWidth
              value={localProfileData.lastName || ''}
              onChange={(e) =>
                setLocalProfileData({ ...localProfileData, lastName: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Username"
              fullWidth
              value={localProfileData.username || ''}
              onChange={(e) =>
                setLocalProfileData({ ...localProfileData, username: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Phone Number"
              fullWidth
              value={localProfileData.phone || localProfileData.phoneNumber || ''}
              onChange={(e) =>
                setLocalProfileData({ ...localProfileData, phone: e.target.value, phoneNumber: e.target.value })
              }
            />
          </Grid>
        </Grid>
        
        
        {/* Password Change Section */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Change Password
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel htmlFor="new-password">New Password</InputLabel>
              <OutlinedInput
                id="new-password"
                type={passwordData.showNewPassword ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange(e, 'newPassword')}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handlePasswordVisibility('new')}
                      edge="end"
                    >
                      {passwordData.showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                }
                label="New Password"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" margin="dense" error={!!passwordError}>
              <InputLabel htmlFor="confirm-password">Confirm Password</InputLabel>
              <OutlinedInput
                id="confirm-password"
                type={passwordData.showConfirmPassword ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange(e, 'confirmPassword')}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handlePasswordVisibility('confirm')}
                      edge="end"
                    >
                      {passwordData.showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Confirm Password"
              />
              {passwordError && <FormHelperText>{passwordError}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Guided tours */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>Guided tours</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {tour.tours?.skippedAll
            ? 'Tours are off. Reset them to see each page\'s tour again the next time you open it.'
            : 'Each page shows a short tour the first time you open it. Reset to see them all again.'}
        </Typography>
        <Button size="small" variant="outlined" onClick={() => { tour.resetAll(); showGlobalNotification('Tours reset. Each page will show its tour again.', 'success'); }}>
          Reset all tours
        </Button>

        {/* Notification Preferences */}
        <Divider sx={{ my: 3 }} />
        <NotificationSettingsPanel
          user={currentUser}
          onPreferencesChange={handleNotificationPreferencesChange}
          isAdminEditing={false}
          disabled={false}
        />
        
        {/* SP-specific fields - only shown if user is an SP */}
        {localProfileData.primaryRole === 'SP' && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Service Provider Information
            </Typography>
            <TextField
              margin="dense"
              label="Company Name"
              fullWidth
              value={localProfileData.companyName || ''}
              onChange={(e) =>
                setLocalProfileData({ ...localProfileData, companyName: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Position"
              fullWidth
              value={localProfileData.position || ''}
              onChange={(e) =>
                setLocalProfileData({ ...localProfileData, position: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Company Address"
              fullWidth
              value={localProfileData.companyAddress || ''}
              onChange={(e) =>
                setLocalProfileData({ ...localProfileData, companyAddress: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Company Address 2 (Optional)"
              fullWidth
              value={localProfileData.companyAddress2 || ''}
              onChange={(e) =>
                setLocalProfileData({ ...localProfileData, companyAddress2: e.target.value })
              }
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="City"
                  fullWidth
                  value={localProfileData.city || ''}
                  onChange={(e) =>
                    setLocalProfileData({ ...localProfileData, city: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="State"
                  fullWidth
                  value={localProfileData.state || ''}
                  onChange={(e) =>
                    setLocalProfileData({ ...localProfileData, state: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  label="Zip"
                  fullWidth
                  value={localProfileData.zip || ''}
                  onChange={(e) =>
                    setLocalProfileData({ ...localProfileData, zip: e.target.value })
                  }
                />
              </Grid>
            </Grid>
            <TextField
              margin="dense"
              label="Main Line Phone Number"
              fullWidth
              value={localProfileData.mainLinePhoneNumber || ''}
              onChange={(e) =>
                setLocalProfileData({
                  ...localProfileData,
                  mainLinePhoneNumber: e.target.value,
                })
              }
            />
            <TextField
              margin="dense"
              label="Dispatch Phone Number"
              fullWidth
              value={localProfileData.dispatchPhoneNumber || ''}
              onChange={(e) =>
                setLocalProfileData({
                  ...localProfileData,
                  dispatchPhoneNumber: e.target.value,
                })
              }
            />
            <TextField
              margin="dense"
              label="Answering Service Phone Number"
              fullWidth
              value={localProfileData.answeringServicePhoneNumber || ''}
              onChange={(e) =>
                setLocalProfileData({
                  ...localProfileData,
                  answeringServicePhoneNumber: e.target.value,
                })
              }
            />
            <TextField
              margin="dense"
              label="Towing License Number"
              fullWidth
              value={localProfileData.towingLicenseNumber || ''}
              onChange={(e) =>
                setLocalProfileData({
                  ...localProfileData,
                  towingLicenseNumber: e.target.value,
                })
              }
            />
            <TextField
              margin="dense"
              label="DOT Number"
              fullWidth
              value={localProfileData.dotNumber || ''}
              onChange={(e) =>
                setLocalProfileData({ ...localProfileData, dotNumber: e.target.value })
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSaveProfile} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditProfileDialog;
